import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import {
    RiAddBoxLine,
    RiCheckboxCircleFill,
    RiFileEditLine
} from 'react-icons/ri';
import TestsVector from 'assets/vectors/Tests.svg';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import axios from 'config/axios';
import { Bundle, Test } from 'global/types';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { updateCart } from 'store/cart';
import { setAlert } from 'store/error';
import './index.css';

type newTestsProps = {
    testType: string;
    tests: Test[];
    bundles: Bundle[];
    numTests: number;
};

function NewTests({ testType, tests, bundles, numTests }: newTestsProps) {
    const [selected, setSelected] = useState([]);

    const { user } = useAppSelector(state => state.user);
    const { tests: cartTests } = useAppSelector(state => state.cart);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const selectTest = (testId: string) => {
        let newSelected = [ ...selected ];
        if (newSelected.includes(testId)) {
            const index = newSelected.indexOf(testId);
            newSelected.splice(index, 1);
        }
        else {
            newSelected.push(testId);
        }
        setSelected(newSelected);
    }

    const selectBundle = (bundle: Bundle) => {
        let newSelected = [...selected.filter(testId => !bundle.tests.some(bundleTest => bundleTest.testId === testId))];
        for (let i = 0; i < bundle.numRequired; i++) {
            newSelected.push(tests.filter(test => bundle.tests.some(bundleTest => bundleTest.testId === test.testId))[i].testId);
        }
        setSelected(newSelected);
    }

    const refreshCart = () => {
        axios.get('payments/cart/')
            .then(res => {
                // console.log(res.data);
                dispatch(updateCart({
                    bundles: res.data?.cartBundles,
                    courses: res.data?.cartCourses,
                    tests: res.data?.cartTests
                }));
            });
    }

    const updateTestsInCart = () => {
        if (user) {
            axios.post(`payments/cart/update/tests/`, {
                    tests: selected,
                    testType: testType
                })
                .then(res => {
                    const { addedTests, removedTests } = res.data;

                    refreshCart();

                    for (let i = 0; i < addedTests.length; i++) {
                        if (addedTests[i].price > 0) {
                            ReactGA.event('add_to_cart', {
                                currency: 'USD',
                                value: addedTests[i].price,
                                items: [{
                                    item_id: addedTests[i].testId,
                                    item_name: addedTests[i].name
                                }]
                            });
                        }
                    }
                    for (let i = 0; i < removedTests.length; i++) {
                        if (removedTests[i].price > 0) {
                            ReactGA.event('remove_from_cart', {
                                currency: 'USD',
                                value: removedTests[i].price,
                                items: [{
                                    item_id: removedTests[i].testId,
                                    item_name: removedTests[i].name
                                }]
                            });
                        }
                    }
                })
                .catch(error => {
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    useEffect(() => {
        setSelected(cartTests.filter(test => test.type === testType && !test.addOn).map(cartTest => cartTest.testId));
    }, [cartTests]);

    const selectedBundle = bundles.sort((a, b) => b.numRequired - a.numRequired).find(bundle => (
        bundle.tests.filter(test => selected.includes(test.testId)).length >= bundle.numRequired
    ));

    return (
        <div className='newTests'>
            <div className='newTests__available'>
                <img className='newTests__availableVector' src={TestsVector} alt='Tests' />
                <h2 className='newTests__availableText'><span>{tests.length}</span> / {numTests} tests available</h2>
            </div>
            {tests.filter(test => test.price === 0).length > 0 && <>
                <h4 className='newTests__subHeader'>Free tests</h4>
                <div className='newTests__tests'>
                    {tests.filter(test => test.price === 0).map(test => (
                        <button className='newTests__test' onClick={() => {
                            if (user) {
                                selectTest(test.testId);
                            }
                            else {
                                navigate('/sign-in', {
                                    state: { referrer: location.pathname }
                                });
                            }
                        }} key={test.testId}>
                            {test.price === 0 && <p className='newTests__freeTest'>FREE!</p>}
                            <div className='newTests__inline'>
                                {/* <RiFileEditLine className='newTests__testIcon' /> */}
                                <div className='newTests__testInfo'>
                                    <p className='newTests__testName'>
                                        {test.name.replace(
                                            'Digital SAT Practice ', ''
                                        ).replace(
                                            'Digital PSAT Practice ', ''
                                        ).replace(
                                            'Digital ACT Practice ', ''
                                        )}
                                        {/* {test.name} */}
                                    </p>
                                    <p className='newTests__testType'>
                                        {test.type}
                                    </p>
                                </div>
                                {selected.includes(test.testId)
                                ?   <RiCheckboxCircleFill
                                        className='newTests__addIcon'
                                    />
                                :   <RiAddBoxLine
                                        className='newTests__addIcon'
                                    />
                                }
                            </div>
                        </button>
                    ))}
                </div>
            </>}
            {bundles.length > 0 && <>
                <h4 className='newTests__subHeader'>Bundle offerings</h4>
                <div className='newTests__bundles'>
                    {bundles.map(bundle => (
                        <div className='newTests__bundle' key={bundle.id}>
                            <div className='newTests__inline'>
                                <RiFileEditLine className='newTests__testIcon' />
                                <p className='newTests__boldText'>
                                    {bundle.name}
                                </p>
                            </div>
                            <p className='newTests__description'>
                                {bundle.description}
                            </p>
                            <div className='newTests__inline'>
                                {bundle.discount &&
                                    // <p className='newTests__discount'>
                                    //     {bundle.discount.type === 'Amount' && '$'}
                                    //     {bundle.discount.value}
                                    //     {bundle.discount.type === 'Percent' && '%'} off
                                    // </p>
                                    <p className='newTests__originalPrice'>
                                        ${bundle.tests.slice(0, bundle.numRequired).reduce((total, test) => total + test.price, 0).toFixed(2)}
                                    </p>
                                }
                                <p className='newTests__boldText'>
                                    ${(bundle.discount?.type === 'Amount'
                                        ?   bundle.tests.slice(0, bundle.numRequired).reduce((total, test) => total + test.price, 0) - bundle.discount.value
                                        :   bundle.discount?.type === 'Percent'
                                        ?   bundle.tests.slice(0, bundle.numRequired).reduce((total, test) => total + test.price, 0)*(100-bundle.discount.value)/100
                                        :   bundle.tests.slice(0, bundle.numRequired).reduce((total, test) => total + test.price, 0)
                                    ).toFixed(2)}
                                </p>
                            </div>
                            {bundle === selectedBundle
                            ?   <Button
                                    className='newTests__button'
                                    disabled
                                >
                                    SELECTED
                                </Button>
                            :   <Button
                                    className='newTests__button'
                                    onClick={() => selectBundle(bundle)}
                                >
                                    SELECT
                                </Button>
                            }
                        </div>
                    ))}
                </div>
            </>}
            {tests.filter(test => test.price > 0).length > 0 && <>
                <h4 className='newTests__subHeader'>Individual purchases</h4>
                <div className='newTests__tests'>
                    {tests.filter(test => test.price > 0).map(test => (
                        <button className='newTests__test' onClick={() => selectTest(test.testId)} key={test.testId}>
                            {test.price === 0 && <p className='newTests__freeTest'>FREE!</p>}
                            <div className='newTests__inline'>
                                {/* <RiFileEditLine className='newTests__testIcon' /> */}
                                <div className='newTests__testInfo'>
                                    <p className='newTests__testName'>
                                        {test.name.replace(
                                            'Digital SAT Practice ', ''
                                        ).replace(
                                            'Digital PSAT Practice ', ''
                                        ).replace(
                                            'Digital ACT Practice ', ''
                                        )}
                                        {/* {test.name} */}
                                    </p>
                                    <p className='newTests__testType'>
                                        {test.type}
                                    </p>
                                </div>
                                {selected.includes(test.testId)
                                ?   <RiCheckboxCircleFill
                                        className='newTests__addIcon'
                                    />
                                :   <RiAddBoxLine
                                        className='newTests__addIcon'
                                    />
                                }
                            </div>
                        </button>
                    ))}
                </div>
            </>}
            {selected.length > 0 && <>
                <h4 className='newTests__subHeader'>Selected tests</h4>
                <div className='newTests__totalWrapper'>
                    <div className='newTests__total'>
                        <h2 className='newTests__numSelected'>
                            {selected.length} tests selected
                        </h2>
                        <div className='newTests__inline'>
                            {/* {selectedBundle && selectedBundle.discount
                            ?   <p className='newTests__discount'>
                                    {selectedBundle.discount.type === 'Amount' && '$'}
                                    {selectedBundle.discount.value}
                                    {selectedBundle.discount.type === 'Percent' && '%'} off
                                </p>
                            :    */}
                            {selectedBundle && selectedBundle.discount || tests.find(test => selected.includes(test.testId) && test.discount)
                            ?   <p className='newTests__originalPrice'>
                                    ${tests.reduce((total, test) => selected.includes(test.testId) ? total + test.price : total, 0).toFixed(2)}
                                </p>
                            :   <></>
                            }
                            <p className='newTests__boldText'>
                                ${selectedBundle && selectedBundle.discount?.type === 'Amount'
                                    ? (tests.reduce((total, test) => selected.includes(test.testId) ? total + test.price : total, 0) - selectedBundle.discount.value).toFixed(2)
                                    : selectedBundle && selectedBundle.discount?.type === 'Percent'
                                    ? (tests.reduce((total, test) => selected.includes(test.testId) ? total + test.price : total, 0)*(100 - selectedBundle.discount.value)/100).toFixed(2)
                                    : tests.reduce((total, test) => selected.includes(test.testId) ? total + (test.discount && test.discount.type === 'Amount' 
                                        ? test.price - test.discount.value
                                        : test.discount && test.discount.type === 'Percent'
                                        ? test.price*(100 - test.discount.value)/100
                                        : test.price
                                      ) : total, 0).toFixed(2)
                                }
                            </p>
                        </div>
                        {selected.length > 0 && (
                            selected.length === cartTests.filter(test => test.type === testType && !test.addOn).length && cartTests.filter(test => test.type === testType && !test.addOn).every(test => selected.includes(test.testId))
                            ?   <Button className='newTests__button' disabled>
                                    ADDED TO CART
                                </Button>
                            :   cartTests.filter(test => test.type === testType && !test.addOn).length > 0
                            ?   <Button className='newTests__button' onClick={updateTestsInCart}>
                                    UPDATE CART
                                </Button>
                            :   <Button className='newTests__button' onClick={updateTestsInCart}>
                                    ADD TO CART
                                </Button>
                        )}
                    </div>
                </div>
            </>}
        </div>
    );
}

export default NewTests;